import { forwardRef } from 'react';

import { useStore } from '@spazious/dollhouse-store';
import { ToolModeEnum } from '@spazious/dollhouse-types';
import { Hint, Typography, useHint } from '@spazious/dollhouse-uikit';
import { useEventCallback, useForkRef } from '@spazious/react-hooks';

import { ToggleButtonLabel, ToggleButtonLabelProps } from '../ToggleButtonLabel/index.js';
import { ToolActionControlPopUp, ToolActionControls } from '../ToolActionControls/index.js';

export interface ToolModeToggleButtonProps extends Omit<ToggleButtonLabelProps, 'label'> {
  minWidth?: string;
  icon: React.ReactNode;
  tool: ToolModeEnum;
  label?: string;
  hint?: string;
  controls?: React.ReactNode;
  controlsPortal?: Element | DocumentFragment;
}
export const ToolModeToggleButton = forwardRef<HTMLButtonElement, ToolModeToggleButtonProps>(
  ({ icon, tool, label, hint, controls, controlsPortal, ...restProps }, fowardedRef) => {
    const selectedTool: string = useStore(({ tool }) => tool);
    const set = useStore(({ set }) => set);

    const [setElement, hintProps] = useHint<HTMLButtonElement>();
    const [setReferenceElement, controlProps] = ToolActionControlPopUp<HTMLButtonElement>();
    const handleClick = useEventCallback(() => {
      set({
        tool: selectedTool === tool ? ToolModeEnum.None : tool,
      });
    });
    const combinedRef = useForkRef(setElement, setReferenceElement);

    return (
      <>
        <ToggleButtonLabel
          ref={useForkRef(combinedRef, fowardedRef)}
          actived={selectedTool === tool}
          onClick={handleClick}
          {...restProps}
          label={
            <Typography size="small" variant="label">
              {label}
            </Typography>
          }
        >
          {icon}
        </ToggleButtonLabel>
        {hint && selectedTool === tool && <Hint {...hintProps}>{hint}</Hint>}
        {controls && selectedTool === tool && (
          <ToolActionControls portal={controlsPortal} {...controlProps}>
            {controls}
          </ToolActionControls>
        )}
      </>
    );
  }
);
